import SchemaFormAutoSubmit from "@/components/Schema/SchemaFormAutoSubmit";
import { PageHeader } from "@ant-design/pro-components";
import { createForm } from "@formily/core";
import { Button, Tabs, Tag, Form } from "antd";
import { useMemo, useRef, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import MMNavigationRoutes from "../../utils/navigationRoutes";
import { Mail, MessageCircle, Phone } from "lucide-react";
import MMLogo from "@/projects/Civi_Circle_Admin/assets/profil.jpg";
import { DynamicModuleService } from "@/services/APIs";
import { notify } from "@/components/common/Notification";
import WorkflowStatus from "@/components/WorkflowStatus";
import { useQuery } from "@tanstack/react-query";

const MemberFormPage: React.FC = () => {
  const navigate = useNavigate();
  const basicInfoSchema = "MM Member";
  // const organizationSchema = "MM Organization"; // Currently unused
  const [searchParams] = useSearchParams();

  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  // Get organization_id from URL parameters
  const organization_id = searchParams.get("organization_id");

  // Function to calculate renewal date based on duration and start date
  const calculateRenewalDate = (duration: string, startDate?: any) => {
    if (!duration) {
      return null;
    }

    // Use provided start date or current date as fallback
    const baseDate = startDate ? dayjs(startDate) : dayjs();
    let renewalDate = baseDate;

    // Parse duration and calculate renewal date
    const durationLower = duration.toLowerCase();

    // Handle lifetime - set to a far future date (e.g., 100 years)
    if (durationLower === "lifetime") {
      renewalDate = baseDate.add(100, "year");
    }
    // Handle year formats (1y, 2y, 1year, 2years, etc.)
    else if (durationLower === "1y") {
      renewalDate = baseDate.add(1, "year");
    }
    // Handle month formats (3m, 6m, etc.)
    else if (durationLower === "3m") {
      renewalDate = baseDate.add(3, "month");
    } else if (durationLower === "6m") {
      renewalDate = baseDate.add(6, "month");
    }
    // Fallback for other formats
    else {
      const yearMatch = durationLower.match(/(\d+)\s*y(?:ear)?s?/);
      if (yearMatch) {
        const years = parseInt(yearMatch[1]);
        renewalDate = baseDate.add(years, "year");
      } else {
        const monthMatch = durationLower.match(/(\d+)\s*m(?:onth)?s?/);
        if (monthMatch) {
          const months = parseInt(monthMatch[1]);
          renewalDate = baseDate.add(months, "month");
        }
      }
    }

    return renewalDate;
  };

  // const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  // const [editingRecord, setEditingRecord] = useState<any>(null);
  // const [setIsEditing] = useState(false);

  // Individual form states for each schema
  // const [setEditingSchema] = useState<string>("");
  // const [setEditingRecordId] = useState<string>("");

  // Manual form state for membership details
  const [membershipForm] = Form.useForm();
  // const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>(""); // Currently unused
  // const [setMembershipDetails] = useState({
  //   organizationProfile: "",
  //   membershipName: "",
  //   membershipType: "",
  //   membershipTypeDisplay: "Individual",
  //   basePrice: "",
  //   duration: "",
  //   startDate: dayjs() as any,
  //   renewalDate: null as any,
  //   benefits: "",
  //   sponsorOrganization: "",
  //   sponsorshipStatus: "",
  //   paymentMethod: "",
  //   paymentDate: null as any,
  //   discountApplied: "",
  //   discountReason: "",
  // });

  // Query for organization data (commented out as not currently used)
  // const { data: organizationData } = useQuery({
  //   queryKey: ["organizationData", organizationSchema],
  //   queryFn: () =>
  //     DynamicModuleService.getGridCoByConfig(
  //       organizationSchema || "",
  //       1,
  //       100,
  //       ["id", "organizationname"],
  //       {}
  //     ),
  //   enabled: !!organizationSchema,
  // });

  // State for membership types
  const membershipTypesSchema = "MM membership type";

  // Query for membership types based on organization_id from URL
  const { data: membershipTypesData, isLoading: isLoadingMembershipTypes } =
    useQuery({
      queryKey: ["membershipTypes", membershipTypesSchema, organization_id],
      queryFn: () =>
        DynamicModuleService.getGridCoByConfig(
          membershipTypesSchema || "",
          1,
          100,
          [
            "id",
            "membershipname",
            "membershiptype",
            "duration",
            "baseprice",
            "benefits",
          ],
          {
            jdid: organization_id,
          }
        ),
      enabled: !!organization_id && !!membershipTypesSchema,
    });

  // Extract membershipTypes array from API response
  const membershipTypesArray = useMemo(() => {
    if (!membershipTypesData) {
      return [];
    }
    // If it's already an array, return it
    if (Array.isArray(membershipTypesData)) {
      return membershipTypesData;
    }
    // If it has a results property (common pagination pattern)
    if (
      membershipTypesData.results &&
      Array.isArray(membershipTypesData.results)
    ) {
      return membershipTypesData.results;
    }
    // If it has a data property
    if (membershipTypesData.data && Array.isArray(membershipTypesData.data)) {
      return membershipTypesData.data;
    }
    // If it's an object but not an array, return empty array
    return [];
  }, [membershipTypesData]);

  // Auto-select membership name if only one option is available
  useEffect(() => {
    if (
      membershipTypesArray &&
      membershipTypesArray.length === 1 &&
      !isLoadingMembershipTypes
    ) {
      const singleMembership = membershipTypesArray[0];
      // Only auto-select if no membership name is currently selected
      const currentMembershipName =
        membershipForm.getFieldValue("membershipName");
      if (!currentMembershipName) {
        // Calculate renewal date based on duration and current start date
        const currentStartDate =
          membershipForm.getFieldValue("startDate") ?? dayjs();
        const renewalDate = calculateRenewalDate(
          singleMembership.duration ?? "",
          currentStartDate
        );

        // Update all related fields
        membershipForm.setFieldsValue({
          membershipName: singleMembership.membershipname,
          membershipType: singleMembership.id ?? "",
          membershipTypeDisplay: "Individual",
          basePrice: singleMembership.baseprice ?? "",
          duration: singleMembership.duration ?? "",
          benefits: singleMembership.benefits ?? "",
          renewalDate: renewalDate,
        });

        // setMembershipDetails(prev => ({
        //   ...prev,
        //   membershipName: singleMembership.membershipname,
        //   membershipType: singleMembership.id ?? "",
        //   basePrice: singleMembership.baseprice ?? "",
        //   duration: singleMembership.duration ?? "",
        //   benefits: singleMembership.benefits ?? "",
        //   renewalDate: renewalDate,
        // }));
      }
    }
  }, [membershipTypesArray, isLoadingMembershipTypes, membershipForm]);

  const formOverview = useMemo(() => createForm({}), []);

  const formOverviewRef = useRef<{ handleSave: () => Promise<void> }>(null);
  const fnameFromUrl = searchParams.get("fname");

  // Alternative: Use a callback to get the created ID
  const handleSaveMember = async () => {
    try {
      // Validate form first
      await formOverview.validate();

      // Manually make the API call using form values
      const formValues = formOverview.values;

      // Make API call directly and use the response ID
      const response = await DynamicModuleService.createItem(
        basicInfoSchema ?? "",
        formValues
      );

      if (response?.id) {
        navigate("/mm" + MMNavigationRoutes.EDIT_MEMBER(response.id));
      } else {
        notify.error("Failed to get member ID after creation");
      }
    } catch (error) {
      console.error("Save error:", error);
      // notify.error("Failed to save member.");
    }
  };
  if (isEditMode) {
    formOverview.setInitialValues({
      id: id,
      jdid: id,
    });
  }

  // Handle edit item - Load data into form instead of navigating
  // const handleEdit = (record: any, schemaName: string) => {
  //   setEditingSchema(schemaName);
  //   setEditingRecordId(record.id);
  //   setIsEditing(true);

  //   // Load the record data into the membership form
  //   if (schemaName === historyMilestonesSchema) {
  //     membershipForm.setFieldsValue({
  //       organizationProfile: record.organization_id ?? "",
  //       membershipName: record.membership_name ?? "",
  //       membershipType: record.membership_type ?? "Individual",
  //       basePrice: record.base_price ?? "",
  //       duration: record.duration ?? "",
  //       startDate: record.start_date ? dayjs(record.start_date) : null,
  //       renewalDate: record.renewal_date ? dayjs(record.renewal_date) : null,
  //       benefits: record.benefits ?? "",
  //       sponsorOrganization: record.sponsor_organization ?? "",
  //       sponsorshipStatus: record.membership_payment_status ?? "",
  //       paymentMethod: record.payment_method ?? "",
  //       paymentDate: record.payment_date ? dayjs(record.payment_date) : null,
  //       discountApplied: record.discount_applied ?? "",
  //       discountReason: record.discount_reason ?? "",
  //     });
  //   }
  // };

  // Cancel editing and reset form
  // const handleCancelEdit = () => {
  //   setIsEditing(false);
  //   setEditingSchema("");
  //   setEditingRecordId("");
  //   // setSelectedOrganizationId("");
  //   // Reset membership form
  //   membershipForm.resetFields();
  //   setMembershipDetails({
  //     organizationProfile: "",
  //     membershipName: "",
  //     membershipType: "",
  //     membershipTypeDisplay: "Individual",
  //     basePrice: "",
  //     duration: "",
  //     startDate: dayjs(),
  //     renewalDate: null,
  //     benefits: "",
  //     sponsorOrganization: "",
  //     sponsorshipStatus: "",
  //     paymentMethod: "",
  //     paymentDate: null,
  //     discountApplied: "",
  //     discountReason: "",
  //   });
  // };

  // const handleDelete = async (id: string, schemaName: string) => {
  //   try {
  //     if (schemaName === historyMilestonesSchema) {
  //       await MembershipTransactionService.deleteMembershipTransaction(id);
  //     } else {
  //       await DynamicModuleService.deleteItem(schemaName, id);
  //     }
  //     refreshTableData();
  //     // notify.success("Record deleted successfully");
  //   } catch (error) {
  //     console.error("Delete error:", error);
  //     notify.error("Failed to delete record");
  //   }
  // };

  // const columnsmilestones: ProColumns<any>[] = [
  //   {
  //     title: "ID",
  //     dataIndex: "id",
  //     hidden: true,
  //   },
  //   {
  //     title: "Membership Name",
  //     dataIndex: "membership_name",
  //     key: "membership_name",
  //   },

  //   {
  //     title: "Base Price",
  //     dataIndex: "base_price",
  //     key: "base_price",
  //   },
  //   {
  //     title: "Duration",
  //     dataIndex: "duration",
  //     key: "duration",
  //   },
  //   // {
  //   //   title: "Start Date",
  //   //   dataIndex: "start_date",
  //   //   key: "start_date",
  //   //   render: (_, record) =>
  //   //     record.start_date ? dayjs(record.start_date).format("YYYY-MM-DD") : "-",
  //   // },
  //   // {
  //   //   title: "Renewal Date",
  //   //   dataIndex: "renewal_date",
  //   //   key: "renewal_date",
  //   //   render: (_, record) =>
  //   //     record.renewal_date
  //   //       ? dayjs(record.renewal_date).format("YYYY-MM-DD")
  //   //       : "-",
  //   // },
  //   {
  //     title: "Benefits",
  //     dataIndex: "benefits",
  //     key: "benefits",
  //     ellipsis: true,
  //   },

  //   {
  //     title: "Payment Status",
  //     dataIndex: "membership_payment_status",
  //     key: "membership_payment_status",
  //     render: (text: any) => (
  //       <div
  //         className={`inline-flex h-9 items-center justify-start gap-2.5 rounded-[50px] px-4 py-[5px] ${
  //           text === "free"
  //             ? "bg-green-100"
  //             : text === "paid"
  //               ? "bg-amber-100"
  //               : "bg-red-100"
  //         }`}
  //       >
  //         <div
  //           className={`justify-start font-['Poppins'] text-sm font-semibold leading-tight ${
  //             text === "free"
  //               ? "text-green-800"
  //               : text === "paid"
  //                 ? "text-amber-800"
  //                 : "text-red-800"
  //           }`}
  //         >
  //           {text}
  //         </div>
  //       </div>
  //     ),
  //   },
  //   {
  //     title: "Payment Method",
  //     dataIndex: "payment_method",
  //     key: "payment_method",
  //   },
  //   // {
  //   //   title: "Payment Date",
  //   //   dataIndex: "payment_date",
  //   //   key: "payment_date",
  //   //   render: (_, record) =>
  //   //     record.payment_date
  //   //       ? dayjs(record.payment_date).format("YYYY-MM-DD")
  //   //       : "-",
  //   // },
  //   // {
  //   //   title: "Discount Applied",
  //   //   dataIndex: "discount_applied",
  //   //   key: "discount_applied",
  //   // },
  //   {
  //     title: "Discount Reason",
  //     dataIndex: "discount_reason",
  //     key: "discount_reason",
  //     ellipsis: true,
  //   },
  //   // {
  //   //   title: "Actions",
  //   //   width: 100,
  //   //   valueType: "option",
  //   //   render: (_, record) => [
  //   //     <Popconfirm
  //   //       title="Are you sure you want to delete this Membership Details?"
  //   //       onConfirm={() => handleDelete(record.id, historyMilestonesSchema)}
  //   //       okText="Yes"
  //   //       key="delete"
  //   //       cancelText="No"
  //   //       disabled={isEditing}
  //   //     >
  //   //       <CustomButton
  //   //         danger
  //   //         key="delete"
  //   //         icon={<Trash size={16} />}
  //   //         type="text"
  //   //         disabled={isEditing}
  //   //       />
  //   //     </Popconfirm>,
  //   //   ],
  //   // },
  // ];

  // Custom tab styling
  const tabStyle = {
    // fontFamily: "Poppins, sans-serif",
    // fontSize: "15px",
    // fontWeight: 500,
  };

  // Generate title based on mode and fname from URL
  const getPageTitle = () => {
    if (isEditMode && fnameFromUrl) {
      return (
        <div
          style={{
            background: "linear-gradient(135deg, #f8f9ff 0%, #e8eaff 1000%)",
            borderRadius: 16,
            padding: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            maxWidth: "100vw",
            boxSizing: "border-box",
          }}
        >
          {/* Left Section */}
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <img src={MMLogo} alt="Member Logo" className="h-20 w-20" />
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <h2
                  style={{
                    fontSize: 24,
                    fontWeight: 600,
                    margin: 0,
                    color: "#1f1f1f",
                  }}
                >
                  {fnameFromUrl}
                </h2>
                <Tag
                  color="success"
                  style={{
                    borderRadius: 20,
                    padding: "6px 16px",
                    fontSize: 13,
                    fontWeight: 500,
                  }}
                >
                  Active
                </Tag>
              </div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 20,
                  color: "#666",
                  fontSize: 14,
                }}
              >
                <span>
                  Member ID{" "}
                  <span style={{ color: "", fontWeight: 500 }}>{id}</span>
                </span>
                <span>
                  Joined{" "}
                  <span style={{ color: "", fontWeight: 500 }}>
                    {new Date().toLocaleDateString("en-US", {
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                </span>
                <span>
                  <span style={{ color: "#635DE8", fontWeight: 500 }}>
                    Gold Member
                  </span>
                </span>
              </div>
            </div>
          </div>

          {/* Right Section: Buttons */}
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              gap: "10px",
              flexShrink: 0,
              paddingLeft: "100px",
            }}
          >
            <Button
              type="text"
              icon={<MessageCircle size={20} />}
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                backgroundColor: "#f0f0ff",
                border: "2px solid #635DE8",
                color: "#635DE8",
              }}
            />
            <Button
              type="text"
              icon={<Phone size={20} />}
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                backgroundColor: "#f0f0ff",
                border: "2px solid #635DE8",
                color: "#635DE8",
              }}
            />
            <Button
              type="text"
              icon={<Mail size={20} />}
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                backgroundColor: "#f0f0ff",
                border: "2px solid #635DE8",
                color: "#635DE8",
              }}
            />
          </div>
        </div>
      );
    }
    return isEditMode ? "Edit Member" : "Add Member";
  };
  return (
    <>
      <PageHeader
        className="custom-page-header"
        title={getPageTitle()}
        onBack={() => navigate("/mm" + MMNavigationRoutes.MEMBER_LIST)}
        extra={
          <WorkflowStatus
            schemaName={basicInfoSchema ?? ""}
            recordId={isEditMode ? (id ?? "") : ""}
          />
        }
      />

      <Tabs
        items={
          isEditMode
            ? [
                {
                  key: "1",
                  label: <span style={tabStyle}>Member Information</span>,
                  children: (
                    <>
                      <SchemaFormAutoSubmit
                        form={formOverview}
                        schemaName={basicInfoSchema ?? ""}
                        recordId={id}
                        ref={formOverviewRef}
                      />
                      <div className="flex w-full justify-end gap-4">
                        <Button
                          type="primary"
                          onClick={async () => {
                            try {
                              await formOverviewRef.current?.handleSave();
                              // notify.success("Member updated successfully");
                            } catch {
                              // notify.error("Failed to update member.");
                            }
                          }}
                        >
                          Update
                        </Button>
                      </div>
                    </>
                  ),
                },
              ]
            : [
                {
                  key: "1",
                  label: <span style={tabStyle}>Member Information</span>,
                  children: (
                    <>
                      <SchemaFormAutoSubmit
                        form={formOverview}
                        schemaName={basicInfoSchema ?? ""}
                        ref={formOverviewRef}
                      />
                      <div className="flex w-full justify-end gap-4">
                        <Button type="primary" onClick={handleSaveMember}>
                          Save
                        </Button>
                      </div>
                    </>
                  ),
                },
              ]
        }
      />
    </>
  );
};

export default MemberFormPage;
